<template>
	<Overlay :data-for="dataFor" :placement="placement" @close="emit('close')">
		<div class="font-size-slider-container">
			<button class="range-step" type="button" :aria-label="label ? `${label} -` : undefined" @click="stepBy(-1)">
				<span class="range-step-icon" aria-hidden="true" v-html="icons[iconStart]" />
			</button>
			<div class="range custom-range-container">
				<div class="range-wrapper">
					<div ref="trackRef" class="range-track" role="slider" tabindex="0"
						 :aria-label="label" :aria-valuemin="steps[0]" :aria-valuemax="steps[steps.length - 1]"
						 :aria-valuenow="steps[currentIndex]"
						 @pointerdown="handlePointerDown" @keydown="handleKeydown">
						<div class="range-progress" :style="{ width: `${percent}%` }" />
						<div class="range-ticks">
							<span v-for="(_, index) in steps" :key="index" :style="{ left: `${stepPercent(index)}%` }" />
						</div>
						<div class="range-thumb" :style="{ left: `${percent}%` }">
							<div class="range-pin">{{ steps[currentIndex] }}</div>
						</div>
					</div>
				</div>
			</div>
			<button class="range-step" type="button" :aria-label="label ? `${label} +` : undefined" @click="stepBy(1)">
				<span class="range-step-icon" aria-hidden="true" v-html="icons[iconEnd]" />
			</button>
		</div>
	</Overlay>
</template>
<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue'
import { icons } from '@shared-assets/icons-v2'
import Overlay from './Overlay.vue'

/**
 * 字級與段距共用的滑桿面板，離散級距、兩端 icon 可點
 */
const props = withDefaults(defineProps<{
	/**
	 * 錨點元素的 id
	 */
	dataFor: string
	/**
	 * 級距，由小到大
	 */
	steps: number[]
	/**
	 * 目前值（v-model）
	 */
	modelValue: number
	/**
	 * 左端 icon 名稱
	 */
	iconStart: keyof typeof icons
	/**
	 * 右端 icon 名稱
	 */
	iconEnd: keyof typeof icons
	/**
	 * 面板開在錨點的上方或下方
	 */
	placement?: 'above' | 'below'
	/**
	 * 無障礙名稱
	 */
	label?: string
}>(), {
	placement: 'above',
	label: undefined,
})

const emit = defineEmits<{
	'update:modelValue': [value: number]
	'close': []
}>()

/**
 * 軌道元素
 */
const trackRef = ref<HTMLElement | null>(null)
/**
 * 拖曳中最後發出的那一階，父層還沒更新 modelValue 時避免重複發出
 */
let dragIndex: number | null = null

/**
 * 目前值最近的那一階，值不在級距內時取最近的
 */
const currentIndex = computed(() => {
	let nearest = 0
	props.steps.forEach((step, index) => {
		if (Math.abs(step - props.modelValue) < Math.abs(props.steps[nearest] - props.modelValue)) {
			nearest = index
		}
	})
	return nearest
})
/**
 * 目前進度百分比
 */
const percent = computed(() => stepPercent(currentIndex.value))

/**
 * 第 index 階在軌道上的位置百分比，只有一階時為 0
 * @param index {number}
 * @return {number}
 */
function stepPercent(index: number): number {
	return props.steps.length > 1 ? (index / (props.steps.length - 1)) * 100 : 0
}

/**
 * 移到第 index 階，超出範圍夾到頭尾，沒有變化就不發出
 * @param index {number}
 * @return {void}
 */
const moveTo = (index: number): void => {
	// 夾在頭尾之間
	const clamped = Math.min(Math.max(index, 0), props.steps.length - 1)
	// 沒有變化就不發出，拖曳中以最後發出的那一階為準
	if (clamped === (dragIndex ?? currentIndex.value)) {
		return
	}

	// 拖曳中記住這一階
	if (dragIndex !== null) {
		dragIndex = clamped
	}
	emit('update:modelValue', props.steps[clamped])
}

/**
 * 往前或往後走一格
 * @param direction {number} -1 或 1
 * @return {void}
 */
const stepBy = (direction: number): void => moveTo(currentIndex.value + direction)

/**
 * 依滑鼠 / 手指的水平位置吸附到最近一階
 * @param clientX {number}
 * @return {void}
 */
const moveToPointer = (clientX: number): void => {
	// 取得軌道位置，量不到寬度就不動
	const rect = trackRef.value?.getBoundingClientRect()
	if (!rect || rect.width <= 0) {
		return
	}

	// 位置換算成比例再吸附到最近一階
	const ratio = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1)
	moveTo(Math.round(ratio * (props.steps.length - 1)))
}

/**
 * 拖曳中的移動
 * @param event {PointerEvent}
 * @return {void}
 */
const handlePointerMove = (event: PointerEvent): void => moveToPointer(event.clientX)

/**
 * 結束拖曳並移除監聽
 * @return {void}
 */
const stopDrag = (): void => {
	dragIndex = null
	window.removeEventListener('pointermove', handlePointerMove)
	window.removeEventListener('pointerup', stopDrag)
	window.removeEventListener('pointercancel', stopDrag)
}

/**
 * 開始拖曳：先吸附到點下的位置，再跟著指標走
 * @param event {PointerEvent}
 * @return {void}
 */
const handlePointerDown = (event: PointerEvent): void => {
	// 避免重複監聽，並清掉上一次拖曳的狀態
	stopDrag()
	// 從目前這一階開始記錄，再吸附到點下的位置
	dragIndex = currentIndex.value
	moveToPointer(event.clientX)
	window.addEventListener('pointermove', handlePointerMove)
	window.addEventListener('pointerup', stopDrag)
	window.addEventListener('pointercancel', stopDrag)
}

/**
 * 鍵盤操作：左右 / 上下走一格，Home / End 到頭尾
 * @param event {KeyboardEvent}
 * @return {void}
 */
const handleKeydown = (event: KeyboardEvent): void => {
	// 依按鍵決定目標階
	const targets: Record<string, number> = {
		ArrowLeft: currentIndex.value - 1,
		ArrowDown: currentIndex.value - 1,
		ArrowRight: currentIndex.value + 1,
		ArrowUp: currentIndex.value + 1,
		Home: 0,
		End: props.steps.length - 1,
	}
	// 不是支援的按鍵就不處理
	if (!(event.key in targets)) {
		return
	}

	// 避免方向鍵捲動頁面
	event.preventDefault()
	moveTo(targets[event.key])
}

// 卸載時移除拖曳監聽
onBeforeUnmount(stopDrag)
</script>
<style lang="scss">
// 兩端 icon 與滑軌排成一列，上下左右各留 8 對齊
.font-size-slider-container {
	display: flex;
	align-items: center;
	gap: 16px;
	padding: 0 8px;

	.range {
		padding: 4px 0;
		width: 100%;
	}
}

// 兩端的 icon 是按鈕，點一下走一格
.range-step {
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	width: 32px;
	height: 32px;
	padding: 0;
	border: none;
	border-radius: 8px;
	background: none;
	color: var(--text-body-01);
	cursor: pointer;

	&:hover {
		background: rgba(21, 21, 21, 0.06);
	}

	&:active {
		background: rgba(21, 21, 21, 0.12);
	}

	.range-step-icon {
		display: inline-flex;
		width: 24px;
		height: 24px;

		svg {
			display: block;
			width: 100%;
			height: 100%;
		}
	}
}

.custom-range-container {
	width: 100%;

	.range-wrapper {
		position: relative;
		width: 100%;
		height: 40px;
		display: flex;
		align-items: center;
	}

	// 軌道比一般滑桿粗，刻度才塞得下
	.range-track {
		position: relative;
		width: 100%;
		height: 8px;
		background-color: rgba(var(--range-background-color), 0.3);
		border-radius: 999px;
		cursor: pointer;
		// 拖曳時不讓瀏覽器接手捲動
		touch-action: none;
	}

	.range-progress {
		position: absolute;
		top: 0;
		left: 0;
		height: 100%;
		background-color: var(--text-body-01);
		border-radius: 8px;
		transition: width 0.1s ease;
	}

	// 刻度排在 progress 後面，黑色與灰色兩段都看得到白點
	.range-ticks {
		position: absolute;
		inset: 0;
		pointer-events: none;

		span {
			position: absolute;
			top: 50%;
			width: 3px;
			height: 3px;
			margin-left: -1.5px;
			border-radius: 50%;
			background: #fff;
			transform: translateY(-50%);
		}

		// 頭尾兩格往內收，不被軌道圓角切到
		span:first-child {
			margin-left: 3px;
		}

		span:last-child {
			margin-left: -6px;
		}
	}

	.range-thumb {
		position: absolute;
		top: 50%;
		width: 16px;
		height: 16px;
		background-color: var(--text-body-01);
		border-radius: 50%;
		transform: translate(-50%, -50%);
		cursor: grab;
		box-shadow: var(--shadow-slider-knob);

		&:active {
			cursor: grabbing;
		}
	}

	// 數值氣泡
	.range-pin {
		position: absolute;
		bottom: 100%;
		left: 50%;
		transform: translateX(-50%);
		margin-bottom: 8px;
		padding: 4px 8px;
		min-width: 24px;
		border-radius: 4px;
		background-color: var(--text-body-01);
		color: #fff;
		font-size: 12px;
		font-weight: bold;
		text-align: center;
		white-space: nowrap;
		animation: pinFadeIn 0.2s ease-out;

		&::after {
			content: '';
			position: absolute;
			top: 100%;
			left: 50%;
			transform: translateX(-50%);
			border-left: 4px solid transparent;
			border-right: 4px solid transparent;
			border-top: 4px solid var(--text-body-01);
		}
	}
}

@keyframes pinFadeIn {
	from {
		opacity: 0;
		transform: translateX(-50%) translateY(5px);
	}
	to {
		opacity: 1;
		transform: translateX(-50%) translateY(0);
	}
}
</style>
